import styled from 'styled-components';
import { Link } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import Fade from 'react-reveal/Fade';

import $ from '../../../styles/global';
import Button from '../../Button';

const Section = styled.section`
  background-color: ${$.color.blue1};

  // NOTE: Desktops and above
  // #region
  @media ${$.device.desktop} {
    padding: ${$.layout().padding1}px 0px;
  }
  // #endregion

  // NOTE: Tablet
  // #region
  @media ${$.device.tablet} {
    padding: ${$.layout().padding1}px 0px;
  }
  // #endregion

  // NOTE: Mobile and below
  // #region
  @media ${$.device.mobile} {
    padding-bottom: ${$.layout().padding1}px;
  }
  // #endregion
`;

const Container = styled.div`
  // NOTE: Desktops and above
  // #region
  @media ${$.device.desktop} {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
    height: 450px;
  }
  // #endregion

  // NOTE: Tablet
  // #region
  @media ${$.device.tablet} {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
    height: 450px;
  }
  // #endregion
`;

const ImageContainer = styled.div`
  // NOTE: Desktops and above
  // #region
  @media ${$.device.desktop} {
    width: calc(50% - ${$.layout().margin2}px / 2);
    height: 100%;
    margin-left: ${$.layout().margin2}px;
    border-radius: ${$.border().radius1}px;
    order: 5;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    mask-image: radial-gradient(#fff, #000);

    & > div {
      border-radius: inherit;
      box-shadow: ${$.dropShadow.normal};
      height: 100%;
      width: 100%;
    }
  }
  // #endregion

  // NOTE: Tablet
  // #region
  @media ${$.device.tablet} {
    width: calc(50% - ${$.layout().margin3}px);
    height: 100%;
    margin-left: ${$.layout().margin3}px;
    border-radius: ${$.border().radius1}px;
    order: 5;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    mask-image: radial-gradient(#fff, #000);

    & > div {
      border-radius: inherit;
      box-shadow: ${$.dropShadow.normal};
      height: 100%;
      width: 100%;
    }
  }
  // #endregion

  // NOTE: Mobile and below
  // #region
  @media ${$.device.mobile} {
    margin-left: -${$.layout().margin3}px;
    margin-bottom: ${$.layout().margin3}px;
    width: 100vw;
  }
  // #endregion
`;

const Title = styled.div`
  font-family: Aileron Heavy;
  color: ${$.color.blue3};

  // NOTE: Desktops and above
  // #region
  @media ${$.device.desktop} {
    width: calc(50% - ${$.layout().margin2}px / 2);
    order: 1;
    font-size: 40px;
  }
  // #endregion

  // NOTE: Tablet
  // #region
  @media ${$.device.tablet} {
    width: 50%;
    order: 1;
    font-size: 28px;
  }
  // #endregion

  // NOTE: Mobile and below
  // #region
  @media ${$.device.mobile} {
    font-size: 30px;
  }
  // #endregion
`;

const Subtitle = styled.div`
  font-family: Cooper Hewitt Semibold;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: ${$.color.orange3};
  line-height: 1.4em;

  // NOTE: Desktops and above
  // #region
  @media ${$.device.desktop} {
    width: calc(50% - ${$.layout().margin2}px / 2);
    order: 3;
  }
  // #endregion

  // NOTE: Tablet
  // #region
  @media ${$.device.tablet} {
    width: 50%;
    order: 3;
    font-size: 18px;
  }
  // #endregion

  // NOTE: Mobile and below
  // #region
  @media ${$.device.mobile} {
    font-size: 18px;
    text-align: center;
    margin-bottom: ${$.layout().margin4}px;
  }
  // #endregion
`;

const Details = styled.div`
  font-family: Lato Regular;
  color: ${$.color.blue5};
  word-break: normal;
  line-height: 1.4em;

  p {
    margin-top: 0;
  }

  // NOTE: Desktops and above
  // #region
  @media ${$.device.desktop} {
    width: calc(50% - ${$.layout().margin2}px / 2);
    order: 2;
  }
  // #endregion

  // NOTE: Tablet
  // #region
  @media ${$.device.tablet} {
    width: 50%;
    order: 2;
    font-size: 17px;
  }
  // #endregion

  // NOTE: Mobile and below
  // #region
  @media ${$.device.mobile} {
    margin-bottom: ${$.layout().margin3}px;
  }
  // #endregion
`;

const StyledButton = styled(({ children, className, to }) => (
  <Link to={to} className={className}>
    <Button
      normalColor={$.color.orange3}
      clickedColor={$.color.blue3}
      normalText={$.color.white}
      clickedText={$.color.white}
      className={className}
    >
      {children}
    </Button>
  </Link>
))`
  // NOTE: Desktops and above
  // #region
  @media ${$.device.desktop} {
    order: 4;
    min-width: 150px;
  }
  // #endregion

  // NOTE: Tablet
  // #region
  @media ${$.device.tablet} {
    order: 4;
    min-width: 150px;
  }
  // #endregion

  // NOTE: Mobile and below
  // #region
  @media ${$.device.mobile} {
    width: 100%;
    margin-bottom: ${$.layout().margin2}px;
  }
  // #endregion
`;

const SolvingProblems = () => (
  <Section>
    <Fade bottom fraction={0.1} distance="100px">
      <Container>
        <ImageContainer>
          <StaticImage
            src="../../../assets/images/whyus/philosophy/solving_problems_through_code.jpg"
            title="Principles 1 - Python Fundamentals"
            alt="Instructor teaching students Python Fundamentals using Minecraft in our Principles 1 class"
          />
        </ImageContainer>
        <Title>Coding is a Means to Solving Problems We Care About</Title>
        <Details>
          <p>
            Code Campers learn to use coding as a tool to build tangible
            products that they know, feel, use and love.
          </p>
          <p>
            Whether it&apos;s in Scratch, AppInventor, Python, JavaScript or
            Java, each of our course offerings incorporates a practical element
            where Code Campers create and build real-world solutions to problems
            that they care about, and that they are proud to call their own.
          </p>
        </Details>
        <Subtitle>SEE WHAT PROBLEMS CODE CAMPERS CARE ABOUT</Subtitle>
        <StyledButton to="/student-achievements/">Learn More</StyledButton>
      </Container>
    </Fade>
  </Section>
);

export default SolvingProblems;
